import {
  createFileRoute,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import { TicketDetail } from "@/components/ticket-detail";
import { CommentThread, type Comment } from "@/components/comment-thread";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { CardTitle, ErrorState } from "@/components/ui/text";
import { HStack } from "@/components/ui/h-stack";
import { PageNavigationHeader } from "@/components/ui/page-navigation-header";
import { Skeleton } from "@/components/ui/skeleton";
import { VStack } from "@/components/ui/v-stack";
import { useToast } from "@/components/ui/toast";
import { apiClient, ApiError } from "@/lib/api/client";
import type { TicketStatus } from "@/lib/api/enums";
import {
  claimTicket,
  releaseTicket,
  getTicketDisplayId,
  markTicketViewed,
  type TicketDetail as TicketData,
} from "@/lib/api/tickets";
import { escalateTicket } from "@/lib/api/action-items";
import { getCurrentUser } from "@/lib/api/auth";
import { formatDate } from "@/lib/formatting";
import { fromIntlLanguage } from "@/lib/i18n";
import { getCommentMode } from "@/lib/ticket-comment-policy";
import { getTicketStatusLabel } from "@/lib/ticket-labels";
import { ticketStatusColor } from "@/lib/status-config";

function TicketDetailPage() {
  const params = Route.useParams();
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const language = fromIntlLanguage(useTranslation().i18n.language);
  const currentUserId = useMemo(() => getCurrentUser()?.id ?? null, []);
  const { t: tCommon } = useTranslation("common");
  const { t: tComments } = useTranslation("comments");
  const { t: tNav } = useTranslation("nav");
  const { t: tTickets } = useTranslation("tickets");
  const { addToast } = useToast();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const {
    data: ticket = null,
    isLoading: loading,
    isError,
    error: ticketError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.ticket(params.id),
    queryFn: async () => {
      const data = await apiClient.get<TicketData>(`/tickets/${params.id}`);
      void markTicketViewed(params.id);
      return data;
    },
    enabled: !!params.id,
  });

  const { data: comments = [] } = useQuery({
    queryKey: ["ticket-comments", params.id],
    queryFn: () => apiClient.get<Comment[]>(`/tickets/${params.id}/comments`),
    enabled: !!params.id,
  });

  const errorMessage = isError
    ? ticketError instanceof Error
      ? ticketError.message
      : null
    : null;

  const handleStatusChange = async (newStatus: TicketStatus) => {
    if (!ticket) return;

    try {
      await apiClient.patch<TicketData>(`/tickets/${ticket.id}`, {
        status: newStatus,
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.ticket(params.id),
      });
    } catch (err) {
      console.error("Error updating ticket status:", err);
      const code = err instanceof ApiError ? err.code : undefined;
      if (code === "INVALID_STATUS_TRANSITION") {
        addToast(tTickets("errors.invalidStatusTransition"), "error");
      } else {
        addToast(tTickets("errors.statusUpdateFailed"), "error");
      }
    }
  };

  const handleDeleteRequest = () => {
    if (!ticket) return;
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!ticket) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/tickets/${ticket.id}`);
      setDeleteConfirmOpen(false);
      void navigate({ to: "/tickets" });
    } catch (err) {
      console.error("Error deleting ticket:", err);
      const code = err instanceof ApiError ? err.code : undefined;
      if (code === "RESTRICTED_ACCESS") {
        addToast(tTickets("errors.deleteRestricted"), "error");
      } else {
        addToast(tTickets("errors.deleteFailed"), "error");
      }
      setDeleteConfirmOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleAddComment = async (content: string, isShared?: boolean) => {
    if (!ticket) return;

    try {
      await apiClient.post<Comment>(`/tickets/${ticket.id}/comments`, {
        content,
        is_shared: isShared ?? false,
      });
      await queryClient.invalidateQueries({
        queryKey: ["ticket-comments", params.id],
      });
    } catch (err) {
      console.error("Error adding comment:", err);
      const code = err instanceof ApiError ? err.code : undefined;
      if (code === "COMMENTS_NOT_ALLOWED") {
        addToast(tTickets("errors.commentsNotAllowed"), "error");
      } else if (code === "COMMENTS_DISABLED_FOR_TICKET") {
        addToast(tTickets("errors.commentsDisabled"), "error");
      } else {
        addToast(tTickets("errors.commentAddFailed"), "error");
      }
    }
  };

  const handleClaim = async () => {
    if (!ticket) return;
    try {
      await claimTicket(ticket.id);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.ticket(params.id),
      });
    } catch (err) {
      console.error("Error claiming ticket:", err);
      const code = err instanceof ApiError ? err.code : undefined;
      if (code === "TICKET_ALREADY_CLAIMED") {
        addToast(tTickets("errors.alreadyClaimed"), "error");
      } else {
        addToast(tTickets("errors.claimFailed"), "error");
      }
    }
  };

  const handleRelease = async () => {
    if (!ticket) return;
    try {
      await releaseTicket(ticket.id);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.ticket(params.id),
      });
    } catch (err) {
      console.error("Error releasing ticket:", err);
      const code = err instanceof ApiError ? err.code : undefined;
      if (code === "NOT_CLAIMED_BY_USER") {
        addToast(tTickets("errors.notClaimedByYou"), "error");
      } else {
        addToast(tTickets("errors.releaseFailed"), "error");
      }
    }
  };

  const handleEscalate = async (reason: string, estimatedImpact?: string) => {
    if (!ticket) return;
    try {
      await escalateTicket(ticket.id, {
        reason,
        estimated_impact: estimatedImpact,
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.ticket(params.id),
      });
    } catch (err) {
      console.error("Error escalating ticket:", err);
      const code = err instanceof ApiError ? err.code : undefined;
      if (code === "ALREADY_ESCALATED") {
        addToast(tTickets("errors.alreadyEscalated"), "error");
      } else {
        // NO_ROUTE_FOUND from escalate.ts:78 actually means "ticket not found"
        // (legacy code-name reuse) — fall through to generic.
        addToast(tTickets("errors.escalateFailed"), "error");
      }
    }
  };

  const handleClose = () => {
    void navigate({ to: "/tickets" });
  };

  const handleBack = () => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      router.history.back();
      return;
    }
    void navigate({ to: "/tickets" });
  };

  if (loading) {
    return (
      <VStack gap="xl" data-testid="ticket-detail-page">
        <PageNavigationHeader
          contextLabel={tNav("tickets")}
          title={<Skeleton className="h-7 w-48" />}
          metadata={tCommon("loading")}
          status={<Skeleton className="h-7 w-24" rounded="full" />}
          onBack={handleBack}
          backAriaLabel={tCommon("back")}
        />

        {/* eslint-disable-next-line design-tokens/no-raw-html-elements -- CSS grid layout with responsive column template */}
        <div
          className="grid grid-cols-1 gap-6 lg:grid-cols-3"
          data-testid="ticket-detail-loading"
        >
          <Card padding="none" className="lg:col-span-2">
            <CardHeader>
              <CardTitle>
                <Skeleton className="h-6 w-64" />
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-5 w-52" />
              <Skeleton className="h-28 w-full" />
            </CardContent>
          </Card>
          <Card padding="none">
            <CardHeader>
              <CardTitle>
                <Skeleton className="h-6 w-32" />
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </Card>
        </div>
      </VStack>
    );
  }

  if (isError || !ticket) {
    return (
      <VStack gap="xl" data-testid="ticket-detail-page">
        <PageNavigationHeader
          contextLabel={tNav("tickets")}
          title={tNav("tickets")}
          metadata=""
          onBack={handleBack}
          backAriaLabel={tCommon("back")}
        />

        <ErrorState
          title={tCommon("errorOccurred")}
          description={
            errorMessage ??
            (isError ? tCommon("errorLoadingData") : tTickets("notFound"))
          }
          action={
            <HStack align="stretch" gap="lg">
              <Button
                color="neutral"
                variant="outline"
                onClick={() => void refetch()}
              >
                {tCommon("retry")}
              </Button>
              <Button
                color="neutral"
                variant="outline"
                onClick={() => void navigate({ to: "/tickets" })}
              >
                {tTickets("backToList")}
              </Button>
            </HStack>
          }
          className="h-96"
          data-testid="ticket-detail-error"
        />
      </VStack>
    );
  }

  const displayId = getTicketDisplayId(
    ticket.ticket_number ?? null,
    ticket.type ?? null,
  );
  const commentMode = getCommentMode(ticket.type);

  return (
    <VStack gap="xl" data-testid="ticket-detail-page">
      <PageNavigationHeader
        contextLabel={tNav("tickets")}
        title={ticket.title}
        metadata={`${displayId} · ${tTickets("created")} ${formatDate(ticket.created_at, language)}`}
        status={
          <Badge color={ticketStatusColor[ticket.status]}>
            {getTicketStatusLabel(ticket.status, tCommon)}
          </Badge>
        }
        onBack={handleBack}
        backAriaLabel={tCommon("back")}
      />

      <TicketDetail
        ticket={ticket}
        currentUserId={currentUserId}
        onStatusChange={handleStatusChange}
        onDelete={ticket.source === "User" ? handleDeleteRequest : undefined}
        onClose={handleClose}
        onClaim={handleClaim}
        onRelease={handleRelease}
        onEscalate={handleEscalate}
        data-testid="ticket-detail"
      />

      <Card padding="none" data-testid="ticket-comments-section">
        <CardHeader>
          <CardTitle>
            {tComments("title")} ({comments.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <CommentThread
            comments={comments}
            onSubmitComment={
              commentMode !== "none" ? handleAddComment : undefined
            }
            sharedToggle={commentMode === "shared_allowed"}
            data-testid="ticket-comments"
          />
        </CardContent>
      </Card>

      <ConfirmDialog
        open={deleteConfirmOpen}
        title={tCommon("delete")}
        description={tTickets("confirmDelete")}
        confirmLabel={tCommon("delete")}
        cancelLabel={tCommon("cancel")}
        loading={deleting}
        onConfirm={() => void handleDeleteConfirm()}
        onCancel={() => setDeleteConfirmOpen(false)}
      />
    </VStack>
  );
}

export const Route = createFileRoute("/_app/tickets/$id")({
  component: TicketDetailPage,
});
